import { splitCategoryFromFacets } from '@app/plp/utils/facetedNavigationUtils';

import {
    ALGOLIA_PERSISTANT_PRODUCT_FILTERS,
    SEARCH_PAGE_PATH,
} from '@libs/algolia';

import type { SearchIndex } from '@libs/algolia';

export const filterOutFacets = (slugs: string[]) => {
    const { categorySlugs } = splitCategoryFromFacets(slugs);
    return categorySlugs;
};

export const filterOutBrandNames = (slugs: string[], brands: string[]) =>
    slugs.filter(
        (s: string) =>
            !decodeURIComponent(s).includes('+') && !brands.includes(s)
    );

// TODO: when we want to stop using the 'test' ID, remove the defaultID fallback in this method
// this will pull the correct ID out of the URL, removing any brand names that may be in it
export const getEntryId = (params: { slug: string[] }, defaultId: string) => {
    // Skip _next/static requests
    // TODO: this is a temporary fix to prevent the server to pass static to /api endpoints
    if (params?.slug?.[0] === '_next') {
        return defaultId;
    }

    const filteredSlugs = filterOutFacets(params.slug);

    if (params?.slug && Array.isArray(filteredSlugs)) {
        return filteredSlugs.join('/');
    }

    return defaultId;
};

const formatSlug = (slug: string): string => slug.toLowerCase();

const location: { [key: string]: string } = {
    'en-US': 'US',
    'en-CA': 'CA',
};

export const mountCategoryQuery = (slug: string[], locale = 'en-US') => {
    // TODO: this is a temporary fix to prevent the server to pass static to /api endpoints
    if (slug?.[0] === '_next') {
        return '';
    }
    if (!slug) {
        return '';
    }
    return `${location[locale]}^^${slug
        .map(str => formatSlug(str))
        .join('^^')}`.toLowerCase();
};

export const getConfigurationFilters = (
    filters: string[],
    preFilterLogic: string,
    algoliaCategoryPath: string
): string => {
    let categoryPrefilters = '';

    // pre-filters defined in Contentful will override category found in URL path.
    if (filters?.length > 0) {
        categoryPrefilters = filters
            .map(category => `custom_category_names:"${category}"`)
            .join(` ${preFilterLogic} `);
    } else if (algoliaCategoryPath !== '') {
        categoryPrefilters = algoliaCategoryPath
            .split(' || ')
            .map(category => `custom_category_names:"${category}"`)
            .join(' OR ');
    }

    if (categoryPrefilters !== '') {
        return `${ALGOLIA_PERSISTANT_PRODUCT_FILTERS} AND (${categoryPrefilters})`;
    }

    return ALGOLIA_PERSISTANT_PRODUCT_FILTERS;
};

type RedirectContent = {
    redirect?: {
        url: string;
    };
};

export const getAlgoliaRedirect = async (query: string, index: SearchIndex) => {
    try {
        if (!(query && query.length)) {
            return null;
        }

        const response = await index.search(query, {
            responseFields: ['renderingContent'],
        });
        return (response?.renderingContent as RedirectContent)?.redirect
            ?.url as string;
    } catch (error) {
        console.error(error);
        return null;
    }
};

export const getAllFacetValues = async (
    index: SearchIndex
): Promise<Record<string, string[]>> => {
    try {
        const response = await index.search('', {
            facets: ['*'],
            maxValuesPerFacet: 2000,
        });
        if (response?.facets) {
            const facetKeys = Object.keys(response.facets);
            const facetValues: Record<string, string[]> = {};

            facetKeys.forEach(key => {
                facetValues[key] = Object.keys(response.facets?.[key] || []);
            });

            return facetValues;
        }

        return {};
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const isSearchPage = (params: { slug: string[] }) =>
    params.slug?.includes(SEARCH_PAGE_PATH) ?? false;
